import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Leaf, Play, Check, Flame, BarChart3, Settings, 
  Sparkles, ChevronRight, Crown, Book, Edit3, Calendar, Target, Mail, Plus, Trash2, Pencil, X, Save, GripVertical 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { 
  celebrateStreakMilestone, 
  celebrateAllActionsComplete,
  checkMilestoneReached,
  getMilestoneMessage 
} from '../utils/confetti';
import AffirmationsModal from '../components/AffirmationsModal';
import Breathwork from '../components/Breathwork';
import RitualCard from '../components/RitualCard';
import FutureSelfLetter from '../components/FutureSelfLetter';
import GratitudeWeekDigest from '../components/GratitudeWeekDigest';
import { trackDailyRoutineCompleted } from '../lib/analytics';
import { hapticLight, hapticSuccess } from '../lib/nativeFeel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format date for display
const formatTargetDate = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

const RITUAL_GLOW = [
  'border-white/5',
  'border-[#C6A75E]/20',
  'border-[#C6A75E]/35 shadow-[0_0_18px_rgba(198,167,94,0.10)]',
  'border-[#C6A75E]/60 shadow-[0_0_32px_rgba(198,167,94,0.28)]',
];

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, activeGoalId } = useAuth();
  const [goal, setGoal] = useState(null);
  const [actions, setActions] = useState([]);
  const [actionStreaks, setActionStreaks] = useState({});
  const [checkin, setCheckin] = useState(null);
  const [streak, setStreak] = useState({ current_streak: 0, longest_streak: 0 });
  const [actionsCompleted, setActionsCompleted] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showMilestoneModal, setShowMilestoneModal] = useState(false);
  const [milestoneData, setMilestoneData] = useState(null);
  const [showAffirmations, setShowAffirmations] = useState(false);
  const [breathworkDone, setBreathworkDone] = useState(false);
  const [affirmationDone, setAffirmationDone] = useState(false);
  const ritualCount = [breathworkDone, affirmationDone, !!checkin?.visualization_completed].filter(Boolean).length;
  const ritualComplete = ritualCount === 3;
  
  // Editing state
  const [isEditingIdentity, setIsEditingIdentity] = useState(false);
  const [identityExpanded, setIdentityExpanded] = useState(false);
  const [isEditingIAm, setIsEditingIAm] = useState(false);
  const [editedIdentity, setEditedIdentity] = useState('');
  const [editedIAmStatements, setEditedIAmStatements] = useState([]);
  
  // Gratitude state
  const [showGratitude, setShowGratitude] = useState(false);
  const [gratitudeEntries, setGratitudeEntries] = useState(['', '', '']);
  const [savingGratitude, setSavingGratitude] = useState(false);
  const [gratitudeCompleted, setGratitudeCompleted] = useState(false);
  const [gratitudeCompletedAt, setGratitudeCompletedAt] = useState(null);
  const [showGratitudeCelebration, setShowGratitudeCelebration] = useState(false);
  const [showGratitudeWeek, setShowGratitudeWeek] = useState(false);

  // Editable Daily Actions state (Premium)
  const [isEditingActions, setIsEditingActions] = useState(false);
  const [editingActionId, setEditingActionId] = useState(null);
  const [editingActionText, setEditingActionText] = useState('');
  const [newActionText, setNewActionText] = useState('');
  const [savingAction, setSavingAction] = useState(false);

  // Drag-and-drop state for action reordering
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  
  // Future Self Letter state
  const [showFutureLetter, setShowFutureLetter] = useState(false);
  
  const previousStreakRef = useRef(0);

  const fetchData = useCallback(async () => {
    try {
      // Fetch active goal - backend will use user's active_goal_id
      const goalRes = await fetch(`${API_URL}/api/goals/active`, {
        credentials: 'include',
      });
      
      if (goalRes.ok) {
        const goalData = await goalRes.json();
        if (!goalData) {
          navigate('/onboarding');
          return;
        }
        setGoal(goalData);

        // Fetch actions for this goal
        const actionsRes = await fetch(`${API_URL}/api/goals/${goalData.goal_id}/actions`, {
          credentials: 'include',
        });
        if (actionsRes.ok) {
          const actionsData = await actionsRes.json();
          setActions(actionsData);
        }

        // Fetch per-action streaks
        const streaksRes = await fetch(`${API_URL}/api/goals/${goalData.goal_id}/actions/streaks`, {
          credentials: 'include',
        });
        if (streaksRes.ok) {
          const streaksData = await streaksRes.json();
          setActionStreaks(streaksData.streaks || {});
        }
      } else {
        navigate('/onboarding');
        return;
      }

      // Fetch today's checkin (scoped to active goal via backend)
      const checkinRes = await fetch(`${API_URL}/api/checkins/today`, {
        credentials: 'include',
      });
      if (checkinRes.ok) {
        const checkinData = await checkinRes.json();
        if (checkinData) {
          setCheckin(checkinData);
          setActionsCompleted(checkinData.actions_completed || {});
        } else {
          // Reset checkin state for new goal
          setCheckin(null);
          setActionsCompleted({});
        }
      }

      // Fetch streak (scoped to active goal via backend)
      const streakRes = await fetch(`${API_URL}/api/streaks`, {
        credentials: 'include',
      });
      if (streakRes.ok) {
        const streakData = await streakRes.json();
        previousStreakRef.current = streakData.current_streak || 0;
        setStreak(streakData);
      }

      // Ritual progress: today's breathwork + affirmation selection
      const [bwRes, affRes] = await Promise.all([
        fetch(`${API_URL}/api/breathwork/streak`, { credentials: 'include' }),
        fetch(`${API_URL}/api/affirmations/today`, { credentials: 'include' }),
      ]);
      setBreathworkDone(bwRes.ok ? !!(await bwRes.json()).data?.completed_today : false);
      setAffirmationDone(affRes.ok ? !!(await affRes.json()).affirmation : false);

      // Fetch today's gratitude completion status
      const gratitudeRes = await fetch(`${API_URL}/api/reflections/gratitude/today`, {
        credentials: 'include',
      });
      if (gratitudeRes.ok) {
        const gratitudeData = await gratitudeRes.json();
        setGratitudeCompleted(!!gratitudeData.daily_gratitude_completed);
        setGratitudeCompletedAt(gratitudeData.completed_at || null);
        // Pre-fill entries if completed today (for review)
        if (gratitudeData.reflection) {
          const r = gratitudeData.reflection;
          setGratitudeEntries([
            r.gratitude_1 || '',
            r.gratitude_2 || '',
            r.gratitude_3 || '',
            r.gratitude_4 || '',
            r.gratitude_5 || ''
          ]);
        } else {
          setGratitudeEntries(['', '', '']);
        }
      }
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Refetch data when activeGoalId changes (goal profile switch)
  useEffect(() => {
    fetchData();
  }, [fetchData, activeGoalId]);

  const toggleAction = async (actionId) => {
    const willComplete = !actionsCompleted[actionId];
    const newCompleted = {
      ...actionsCompleted,
      [actionId]: willComplete,
    };
    setActionsCompleted(newCompleted);
    // Native haptic on completion tick (no-op on web).
    if (willComplete) hapticLight();

    // Auto-save checkin
    await saveCheckin(newCompleted, null);
  };

  const saveCheckin = async (completed, journal) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/checkins`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          actions_completed: completed,
          visualization_completed: checkin?.visualization_completed || false,
          journal_entry: journal || null,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setCheckin(data);
        
        // Refresh per-action streaks (lightweight)
        if (goal?.goal_id) {
          fetch(`${API_URL}/api/goals/${goal.goal_id}/actions/streaks`, { credentials: 'include' })
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (d) setActionStreaks(d.streaks || {}); })
            .catch(() => {});
        }
        
        // Check if all actions completed
        const allCompleted = actions.every(a => completed[a.action_id]);
        if (allCompleted && actions.length > 0) {
          // Subtle celebration for completing all actions
          celebrateAllActionsComplete();
          hapticSuccess();
          // Fire the approved analytics event (no PII)
          trackDailyRoutineCompleted({ actions_count: actions.length });
          
          // Refresh streak
          const streakRes = await fetch(`${API_URL}/api/streaks`, {
            credentials: 'include',
          });
          if (streakRes.ok) {
            const streakData = await streakRes.json();
            
            // Check for milestone
            const milestone = checkMilestoneReached(
              streakData.current_streak, 
              previousStreakRef.current
            );
            
            if (milestone) {
              const message = getMilestoneMessage(milestone);
              setMilestoneData(message);
              setShowMilestoneModal(true);
              celebrateStreakMilestone(milestone);
            } else if (streakData.current_streak > previousStreakRef.current) {
              toast.success(`Streak extended to ${streakData.current_streak} days!`);
            }
            
            previousStreakRef.current = streakData.current_streak;
            setStreak(streakData);
          }
        }
      }
    } catch (error) {
      // Silent fail - non-critical
    } finally {
      setSaving(false);
    }
  };

  // Save edited identity statement
  const saveIdentity = async () => {
    if (!editedIdentity.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/goals/${goal.goal_id}/identity`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ identity_statement: editedIdentity.trim() })
      });
      if (res.ok) {
        setGoal({ ...goal, identity_statement: editedIdentity.trim() });
        setIsEditingIdentity(false);
        toast.success('Identity statement updated');
      }
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  // Save edited I Am statements
  const saveIAmStatements = async () => {
    const validStatements = editedIAmStatements.filter(s => s.trim());
    if (validStatements.length < 3) {
      toast.error('Minimum 3 I Am statements required');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/goals/${goal.goal_id}/i-am-statements`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ i_am_statements: validStatements })
      });
      if (res.ok) {
        setGoal({ ...goal, i_am_statements: validStatements });
        setIsEditingIAm(false);
        toast.success('I Am statements updated');
      }
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  // Daily Actions CRUD (Premium)
  const startEditAction = (action) => {
    setEditingActionId(action.action_id);
    setEditingActionText(action.action_text);
  };

  const cancelEditAction = () => {
    setEditingActionId(null);
    setEditingActionText('');
  };

  const saveActionEdit = async (actionId) => {
    if (!editingActionText.trim()) {
      toast.error('Action text cannot be empty');
      return;
    }
    setSavingAction(true);
    try {
      const res = await fetch(`${API_URL}/api/goals/${goal.goal_id}/actions/${actionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action_text: editingActionText.trim() })
      });
      if (res.ok) {
        setActions(actions.map(a => a.action_id === actionId ? { ...a, action_text: editingActionText.trim() } : a));
        toast.success('Action updated');
        cancelEditAction();
      } else if (res.status === 403) {
        toast.error('Premium subscription required to edit actions');
      } else {
        toast.error('Failed to update action');
      }
    } catch (error) {
      toast.error('Failed to update action');
    } finally {
      setSavingAction(false);
    }
  };

  const deleteAction = async (actionId) => {
    if (!window.confirm('Delete this action?')) return;
    setSavingAction(true);
    try {
      const res = await fetch(`${API_URL}/api/goals/${goal.goal_id}/actions/${actionId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        setActions(actions.filter(a => a.action_id !== actionId));
        const newCompleted = { ...actionsCompleted };
        delete newCompleted[actionId];
        setActionsCompleted(newCompleted);
        toast.success('Action deleted');
      } else if (res.status === 403) {
        toast.error('Premium subscription required to delete actions');
      } else {
        toast.error('Failed to delete action');
      }
    } catch (error) {
      toast.error('Failed to delete action');
    } finally {
      setSavingAction(false);
    }
  };

  const addAction = async () => {
    if (!newActionText.trim()) {
      toast.error('Please enter an action');
      return;
    }
    if (actions.length >= 5) {
      toast.error('Maximum 5 daily actions');
      return;
    }
    setSavingAction(true);
    try {
      const res = await fetch(`${API_URL}/api/goals/${goal.goal_id}/actions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action_text: newActionText.trim() })
      });
      if (res.ok) {
        const newAction = await res.json();
        setActions([...actions, newAction]);
        setNewActionText('');
        toast.success('Action added');
      } else if (res.status === 403) {
        toast.error('Premium subscription required to add actions');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to add action');
      }
    } catch (error) {
      toast.error('Failed to add action');
    } finally {
      setSavingAction(false);
    }
  };

  // Persist a new order to the backend (optimistic UI: actions state already reordered)
  const persistActionOrder = async (orderedActions) => {
    try {
      const res = await fetch(`${API_URL}/api/goals/${goal.goal_id}/actions/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action_ids: orderedActions.map(a => a.action_id) })
      });
      if (!res.ok) {
        if (res.status === 403) {
          toast.error('Premium subscription required to reorder');
        } else {
          toast.error('Failed to save new order');
        }
        // Roll back by refetching
        const actionsRes = await fetch(`${API_URL}/api/goals/${goal.goal_id}/actions`, { credentials: 'include' });
        if (actionsRes.ok) setActions(await actionsRes.json());
      }
    } catch (error) {
      toast.error('Failed to save new order');
    }
  };

  // Drag-and-drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Required for Firefox
    try { e.dataTransfer.setData('text/plain', String(index)); } catch (_) { /* ignore */ }
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (index !== dragOverIndex) setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const fromIndex = draggedIndex;
    setDraggedIndex(null);
    setDragOverIndex(null);
    if (fromIndex === null || fromIndex === dropIndex) return;
    const reordered = [...actions];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    setActions(reordered);
    persistActionOrder(reordered);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Save gratitude
  const saveGratitude = async () => {
    const validGratitude = gratitudeEntries.filter(g => g.trim());
    if (validGratitude.length < 3) {
      toast.error('Please enter at least 3 things you\'re grateful for');
      return;
    }
    setSavingGratitude(true);
    try {
      const res = await fetch(`${API_URL}/api/reflections/gratitude`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          gratitude_1: gratitudeEntries[0] || '',
          gratitude_2: gratitudeEntries[1] || '',
          gratitude_3: gratitudeEntries[2] || '',
          gratitude_4: gratitudeEntries[3] || null,
          gratitude_5: gratitudeEntries[4] || null
        })
      });
      if (res.ok) {
        const data = await res.json();
        setShowGratitude(false);
        setGratitudeCompleted(!!data.daily_gratitude_completed);
        setGratitudeCompletedAt(data.completed_at || new Date().toISOString());
        setShowGratitudeCelebration(true);
        hapticSuccess();
      }
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSavingGratitude(false);
    }
  };

  const completedCount = actions.filter(a => actionsCompleted[a.action_id]).length;
  const todayPercent = actions.length ? Math.round((completedCount / actions.length) * 100) : 0;
  const completionPercentage = actions.length > 0 ? Math.round((completedCount / actions.length) * 100) : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1C1C1E] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[#8BAA9E] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#B5B1AB] font-sans">Loading your ritual...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1C1C1E] pb-24" data-testid="dashboard-page">
      {/* Milestone Celebration Modal */}
      {showMilestoneModal && milestoneData && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm animate-fade-in">
          <div className="bg-[#252528] border border-[#C6A75E]/30 rounded-[32px] p-10 max-w-sm mx-6 text-center transform animate-slide-up">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#C6A75E]/30 to-[#C6A75E]/10 flex items-center justify-center mx-auto mb-6 animate-pulse">
              <Crown className="w-12 h-12 text-[#C6A75E]" strokeWidth={1.5} />
            </div>
            <h2 className="font-serif text-3xl text-[#C6A75E] mb-2">{milestoneData.title}</h2>
            <p className="text-[#DCD3CA] font-sans mb-8">{milestoneData.subtitle}</p>
            <Button
              onClick={() => setShowMilestoneModal(false)}
              className="bg-[#C6A75E] hover:bg-[#D4B86E] text-[#1C1C1E] rounded-full px-8 py-4 font-medium"
              data-testid="close-milestone-modal"
            >
              Continue Journey
            </Button>
          </div>
        </div>
      )}

      {/* AI Affirmations Modal */}
      {showAffirmations && goal && (
        <AffirmationsModal 
          goal={goal}
          isPremium={user?.is_premium}
          onClose={() => setShowAffirmations(false)}
          onSelect={() => setAffirmationDone(true)}
        />
      )}

      {/* Gratitude Completion Celebration Modal */}
      {showGratitudeCelebration && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm animate-fade-in px-6"
          data-testid="gratitude-celebration-modal"
          onClick={() => setShowGratitudeCelebration(false)}
        >
          <div
            className="bg-gradient-to-br from-[#252528] via-[#252528] to-[#1F1F22] border border-[#C6A75E]/40 rounded-[32px] p-10 max-w-sm mx-auto text-center transform animate-slide-up relative overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute -top-20 -right-20 w-48 h-48 bg-[#C6A75E]/20 rounded-full blur-[60px]" />
            <div className="absolute -bottom-20 -left-20 w-48 h-48 bg-[#8BAA9E]/10 rounded-full blur-[60px]" />
            <div className="relative z-10">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#C6A75E]/30 to-[#C6A75E]/10 flex items-center justify-center mx-auto mb-6 animate-pulse">
                <Sparkles className="w-10 h-10 text-[#C6A75E]" strokeWidth={1.5} />
              </div>
              <p className="text-[#C6A75E] text-xs tracking-[0.25em] uppercase font-sans mb-3">
                ✨ Gratitude Practice Complete
              </p>
              <p className="font-serif text-[#DCD3CA] text-base leading-relaxed mb-6">
                Every time you practice gratitude, you train your brain to notice opportunities, strengths, and moments of joy more easily.
              </p>
              <p className="font-serif text-[#C6A75E] text-lg italic mb-2">
                Small practice. Lasting impact.
              </p>
              <p className="font-serif text-[#DCD3CA] text-xl mb-8">
                Well done.
              </p>
              <Button
                onClick={() => setShowGratitudeCelebration(false)}
                className="bg-[#C6A75E] hover:bg-[#D4B86E] text-[#1C1C1E] rounded-full px-8 py-4 font-medium"
                data-testid="close-gratitude-celebration"
              >
                Continue
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="px-5 pt-8 pb-4 max-w-2xl mx-auto">
        <div className="flex items-start justify-between gap-4">
          <h1 className="font-serif text-[34px] sm:text-[40px] leading-[1.08] text-[#DCD3CA]" data-testid="dashboard-greeting">
            Welcome to your Future Self{' '}
            <Sparkles className="inline w-6 h-6 text-[#C6A75E] -mt-2" strokeWidth={1.5} />
          </h1>
          <button
            onClick={() => navigate('/settings')}
            className="w-11 h-11 shrink-0 rounded-full bg-[#252528] border border-white/5 flex items-center justify-center text-[#B5B1AB] hover:text-[#DCD3CA] transition-colors"
            data-testid="settings-btn"
            aria-label="Settings"
          >
            <Settings className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>
        <button
          onClick={() => navigate('/settings#goals-section')}
          className="mt-3 flex items-center gap-1 text-left font-serif text-[19px] text-[#DCD3CA] hover:text-white transition-colors"
          data-testid="goal-title-btn"
        >
          <span>{goal?.title}</span>
          <ChevronRight className="w-4 h-4 text-[#B5B1AB] shrink-0" />
        </button>
        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] font-sans" data-testid="dashboard-stats-row">
          <Flame className="w-4 h-4 text-[#C6A75E]" strokeWidth={1.5} />
          <span className="text-[#C6A75E]" data-testid="streak-count">{streak.current_streak} day streak</span>
          <span className="text-[#7A7670]">•</span>
          <span className="text-[#B5B1AB]" data-testid="today-progress">{todayPercent}% of today&apos;s actions complete</span>
          {goal?.target_date && (
            <span className="flex items-center gap-1 text-[#7A7670]" data-testid="goal-target-date">
              <span>•</span>
              <Calendar className="w-3.5 h-3.5 text-[#8BAA9E]" strokeWidth={1.5} />
              Target {formatTargetDate(goal.target_date)}
            </span>
          )}
        </div>
      </header>

      <div className="px-4 space-y-4 max-w-2xl mx-auto">
        {/* Your Future Self */}
        <div className="relative bg-[#252528] border border-white/5 rounded-[22px] overflow-hidden" data-testid="identity-card">
          <img src="/images/home/identity.jpg" alt="" className="absolute inset-y-0 right-0 h-full w-[62%] object-cover opacity-80 pointer-events-none" />
          <div className={`absolute inset-0 pointer-events-none bg-gradient-to-r ${identityExpanded ? 'from-[#252528] via-[#252528]/95 to-[#252528]/80' : 'from-[#252528] via-[#252528]/85 to-transparent'}`} />
          <div className={`relative z-10 p-5 ${identityExpanded ? '' : 'pr-[28%]'}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[#B5B1AB] text-[11px] tracking-[0.25em] uppercase font-sans">Your Future Self</p>
              {identityExpanded && !isEditingIdentity && (
                <button
                  onClick={() => { setEditedIdentity(goal?.identity_statement || ''); setIsEditingIdentity(true); }}
                  className="text-[#7A7670] hover:text-[#8BAA9E]"
                  data-testid="edit-identity-btn"
                  aria-label="Edit identity"
                >
                  <Edit3 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              )}
            </div>
            {isEditingIdentity ? (
              <div className="space-y-3">
                <Textarea
                  value={editedIdentity}
                  onChange={(e) => setEditedIdentity(e.target.value)}
                  className="bg-[#1C1C1E] border-white/10 text-[#DCD3CA] font-serif text-lg"
                  rows={4}
                  data-testid="edit-identity-textarea"
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={saveIdentity} className="bg-[#8BAA9E] text-[#1C1C1E]" data-testid="save-identity-btn">Save</Button>
                  <Button size="sm" variant="outline" onClick={() => setIsEditingIdentity(false)} className="border-[#666666] text-[#B5B1AB]" data-testid="cancel-identity-btn">Cancel</Button>
                </div>
              </div>
            ) : (
              <p className={`font-serif text-[19px] leading-snug text-[#DCD3CA] ${identityExpanded ? '' : 'line-clamp-3'}`} data-testid="identity-statement">
                &ldquo;{goal?.identity_statement}&rdquo;
              </p>
            )}

            {identityExpanded && (goal?.i_am_statements?.length > 0 || isEditingIAm) && (
              <div className="mt-5 pt-4 border-t border-white/10" data-testid="i-am-card">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[#C6A75E] text-[11px] tracking-[0.25em] uppercase font-sans">I Am Statements</p>
                  {!isEditingIAm && (
                    <button
                      onClick={() => { setEditedIAmStatements([...(goal?.i_am_statements || []), '', ''].slice(0, 5)); setIsEditingIAm(true); }}
                      className="text-[#7A7670] hover:text-[#C6A75E]"
                      data-testid="edit-iam-btn"
                      aria-label="Edit I Am statements"
                    >
                      <Edit3 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  )}
                </div>
                {isEditingIAm ? (
                  <div className="space-y-3">
                    {editedIAmStatements.map((statement, idx) => (
                      <div key={`iam-edit-${idx}`} className="flex items-center gap-2">
                        <span className="text-[#C6A75E] font-serif text-sm w-16">I am</span>
                        <Input
                          value={statement}
                          onChange={(e) => {
                            const newStatements = [...editedIAmStatements];
                            newStatements[idx] = e.target.value;
                            setEditedIAmStatements(newStatements);
                          }}
                          placeholder={idx < 3 ? 'Required' : 'Optional'}
                          className="flex-1 bg-[#1C1C1E] border-white/10 text-[#DCD3CA]"
                        />
                      </div>
                    ))}
                    <div className="flex gap-2 pt-2">
                      <Button size="sm" onClick={saveIAmStatements} className="bg-[#C6A75E] text-[#1C1C1E]">Save</Button>
                      <Button size="sm" variant="outline" onClick={() => setIsEditingIAm(false)} className="border-[#666666] text-[#B5B1AB]">Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {goal?.i_am_statements?.map((statement, idx) => (
                      <p key={`iam-${idx}-${statement.slice(0,10)}`} className="font-serif text-[#DCD3CA] text-base">
                        <span className="text-[#C6A75E]">I am</span> {statement}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {!isEditingIdentity && (
              <button
                onClick={() => { setIdentityExpanded(!identityExpanded); setIsEditingIAm(false); }}
                className="mt-3 flex items-center gap-1 text-[#C6A75E] text-[15px] font-sans hover:text-[#D4B872] transition-colors"
                data-testid="read-full-identity-btn"
              >
                {identityExpanded ? 'Show less' : 'Read full identity'}
                <ChevronRight className={`w-4 h-4 transition-transform ${identityExpanded ? '-rotate-90' : ''}`} />
              </button>
            )}
          </div>
        </div>

        {/* Letter From Your Future Self */}
        <button
          onClick={() => setShowFutureLetter(true)}
          className="w-full flex items-center justify-between px-5 py-4 bg-[#252528] border border-[#C6A75E]/20 rounded-[18px] hover:border-[#C6A75E]/45 transition-colors"
          data-testid="future-letter-btn"
        >
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-[#C6A75E]" strokeWidth={1.5} />
            <span className="text-[#DCD3CA] font-serif text-[16px]">Letter From Your Future Self</span>
          </div>
          <ChevronRight className="w-4 h-4 text-[#C6A75E]" />
        </button>

        {/* Today's Actions */}
        <div className="bg-[#252528] border border-white/5 rounded-[22px] p-4 sm:p-5" data-testid="actions-card">
          <div className="flex items-center justify-between px-1 mb-3">
            <span className="text-[#B5B1AB] text-[12px] tracking-[0.3em] uppercase font-sans">Today&apos;s Actions</span>
            <div className="flex items-center gap-3">
              {!isEditingActions && (
                <span className="text-[#B5B1AB] text-sm font-sans" data-testid="actions-count">{completedCount}/{actions.length}</span>
              )}
              {user?.is_premium ? (
                <button
                  onClick={() => { setIsEditingActions(!isEditingActions); cancelEditAction(); setNewActionText(''); }}
                  className="text-[#7A7670] hover:text-[#8BAA9E] flex items-center gap-1"
                  data-testid="toggle-edit-actions-btn"
                  aria-label={isEditingActions ? 'Done editing actions' : 'Edit actions'}
                >
                  {isEditingActions ? <X className="w-4 h-4" strokeWidth={1.5} /> : <Pencil className="w-4 h-4" strokeWidth={1.5} />}
                </button>
              ) : (
                <span className="flex items-center gap-1 text-[#C6A75E] text-xs font-sans" data-testid="actions-premium-lock">
                  <Crown className="w-3 h-3" /> Edit
                </span>
              )}
            </div>
          </div>
          {!isEditingActions && (
            <div className="mx-1 mb-3 h-2 rounded-full bg-white/5 overflow-hidden" data-testid="actions-progress-bar">
              <div className="h-full rounded-full bg-[#8BAA9E] transition-[width] duration-500" style={{ width: `${todayPercent}%` }} />
            </div>
          )}
          <div className={isEditingActions ? 'space-y-3' : 'rounded-[16px] bg-[#2A2A2D] divide-y divide-white/5 overflow-hidden'}>
            {actions.map((action, index) => {
              const isEditingThis = isEditingActions && editingActionId === action.action_id;
              if (isEditingThis) {
                return (
                  <div
                    key={action.action_id}
                    className="flex items-center gap-2 p-3 rounded-xl bg-[#1C1C1E] border border-[#8BAA9E]/30"
                    data-testid={`action-edit-row-${action.action_id}`}
                  >
                    <Input
                      value={editingActionText}
                      onChange={(e) => setEditingActionText(e.target.value)}
                      className="flex-1 bg-[#252528] border-white/10 text-[#DCD3CA]"
                      placeholder="Action text"
                      data-testid={`action-edit-input-${action.action_id}`}
                    />
                    <Button
                      size="sm"
                      onClick={() => saveActionEdit(action.action_id)}
                      disabled={savingAction}
                      className="bg-[#8BAA9E] text-[#1C1C1E] hover:bg-[#A3C2B6]"
                      data-testid={`save-action-${action.action_id}`}
                    >
                      <Save className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={cancelEditAction}
                      className="border-[#666666] text-[#B5B1AB]"
                      data-testid={`cancel-edit-action-${action.action_id}`}
                    >
                      <X className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                );
              }

              if (isEditingActions) {
                const isDragging = draggedIndex === index;
                const isDragOver = dragOverIndex === index && draggedIndex !== null && draggedIndex !== index;
                return (
                  <div
                    key={action.action_id}
                    draggable={actions.length > 1}
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                    className={`flex items-center gap-2 p-4 rounded-xl bg-[#1C1C1E] border transition-all ${
                      isDragging ? 'opacity-40 border-[#8BAA9E]' :
                      isDragOver ? 'border-[#8BAA9E] bg-[#8BAA9E]/5' :
                      'border-white/5'
                    }`}
                    data-testid={`action-row-${action.action_id}`}
                  >
                    {actions.length > 1 && (
                      <span
                        className="text-[#7A7670] hover:text-[#8BAA9E] cursor-grab active:cursor-grabbing touch-none"
                        data-testid={`drag-handle-${action.action_id}`}
                        aria-label="Drag to reorder"
                      >
                        <GripVertical className="w-4 h-4" strokeWidth={1.5} />
                      </span>
                    )}
                    <span className="flex-1 font-sans text-[#DCD3CA]">{action.action_text}</span>
                    <button
                      onClick={() => startEditAction(action)}
                      className="text-[#7A7670] hover:text-[#8BAA9E] p-2"
                      data-testid={`edit-action-${action.action_id}`}
                      aria-label="Edit action"
                    >
                      <Pencil className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={() => deleteAction(action.action_id)}
                      disabled={savingAction || actions.length <= 1}
                      className="text-[#7A7670] hover:text-red-400 p-2 disabled:opacity-30 disabled:cursor-not-allowed"
                      data-testid={`delete-action-${action.action_id}`}
                      aria-label="Delete action"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                );
              }

              const done = actionsCompleted[action.action_id];
              const s = actionStreaks[action.action_id];
              return (
                <button
                  key={action.action_id}
                  onClick={() => toggleAction(action.action_id)}
                  className="w-full flex items-center gap-4 px-4 py-3.5 text-left hover:bg-white/[0.03] transition-colors"
                  data-testid={`action-${action.action_id}`}
                >
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                    done ? 'bg-[#8BAA9E]' : 'border-2 border-[#7A7670]'
                  }`}>
                    {done && <Check className="w-4 h-4 text-[#1C1C1E]" strokeWidth={2.5} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="font-sans block text-[15px] leading-snug text-[#DCD3CA]">{action.action_text}</span>
                    {s && (s.current_streak > 0 || s.longest_streak > 0) && (
                      <span
                        className="flex items-center gap-2 mt-1 text-xs font-sans text-[#7A7670]"
                        data-testid={`action-streak-${action.action_id}`}
                      >
                        {s.current_streak > 0 && (
                          <span className="flex items-center gap-1 text-[#C6A75E]">
                            <Flame className="w-3 h-3" strokeWidth={1.5} />
                            {s.current_streak}d streak
                          </span>
                        )}
                        {s.longest_streak > s.current_streak && <span>· best {s.longest_streak}d</span>}
                        {s.completion_rate_30d > 0 && <span className="text-[#8BAA9E]/70">· {s.completion_rate_30d}% 30d</span>}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}

            {isEditingActions && actions.length < 5 && (
              <div
                className="flex items-center gap-2 p-3 rounded-xl bg-[#1C1C1E] border border-dashed border-white/10"
                data-testid="add-action-row"
              >
                <Plus className="w-4 h-4 text-[#8BAA9E]" strokeWidth={1.5} />
                <Input
                  value={newActionText}
                  onChange={(e) => setNewActionText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addAction(); }}
                  placeholder="Add a new daily action..."
                  className="flex-1 bg-[#252528] border-white/10 text-[#DCD3CA]"
                  data-testid="new-action-input"
                />
                <Button
                  size="sm"
                  onClick={addAction}
                  disabled={savingAction || !newActionText.trim()}
                  className="bg-[#8BAA9E] text-[#1C1C1E] hover:bg-[#A3C2B6]"
                  data-testid="add-action-btn"
                >
                  Add
                </Button>
              </div>
            )}

            {isEditingActions && (
              <p className="text-[#7A7670] text-xs font-sans text-center pt-2">
                {actions.length}/5 actions • Drag to reorder • Tap pencil to edit, trash to delete
              </p>
            )}
          </div>
          <button
            onClick={() => navigate('/progress')}
            className="w-full flex items-center gap-3 px-2 pt-4 pb-1 text-left text-[#B5B1AB] hover:text-[#DCD3CA] transition-colors"
            data-testid="view-progress-btn"
          >
            <BarChart3 className="w-4 h-4 text-[#8BAA9E]" strokeWidth={1.5} />
            <span className="flex-1 text-[14px] font-sans">View streaks &amp; activity history</span>
            <ChevronRight className="w-4 h-4 text-[#7A7670]" />
          </button>
        </div>

        {/* Your Daily Ritual */}
        <section
          className={`bg-[#252528] border rounded-[22px] p-3 sm:p-4 transition-[border-color,box-shadow] duration-700 ${RITUAL_GLOW[ritualCount]}`}
          data-testid="daily-ritual-section"
          data-ritual-progress={ritualCount}
          data-ritual-complete={ritualComplete ? 'true' : 'false'}
        >
          <div className="px-2 pt-2 pb-3">
            <h2
              className={`text-[12px] tracking-[0.3em] uppercase font-sans transition-colors duration-700 ${ritualComplete ? 'text-[#E3C98A] [text-shadow:0_0_14px_rgba(198,167,94,0.55)]' : 'text-[#B5B1AB]'}`}
              data-testid="daily-ritual-heading"
            >Your Daily Ritual</h2>
            <p className={`text-[14px] font-sans mt-1 ${ritualComplete ? 'text-[#C6A75E]' : 'text-[#B5B1AB]'}`} data-testid="daily-ritual-subtitle">
              {ritualComplete ? 'Ritual complete — beautifully done.' : 'Take a moment to connect with your future self.'}
            </p>
          </div>
          <div className="space-y-2.5">
            <Breathwork
              done={breathworkDone}
              onSessionComplete={() => setBreathworkDone(true)}
              onComplete={() => setShowAffirmations(true)}
              onClose={() => {}}
            />
            <RitualCard
              index={2}
              title="Today’s Affirmation"
              subtitle="Choose your daily affirmation"
              image="/images/home/affirmation.jpg"
              actionLabel="Choose"
              tone="gold"
              locked={!user?.is_premium}
              done={affirmationDone}
              testId="select-affirmation-btn"
              onClick={() => setShowAffirmations(true)}
            />
            <RitualCard
              index={3}
              title="Visualisation"
              subtitle="Connect with your future self"
              image="/images/home/visualisation.jpg"
              actionLabel="Begin"
              done={checkin?.visualization_completed}
              testId="start-visualization-btn"
              onClick={() => navigate('/visualization')}
            />
          </div>
        </section>

        {/* Reflect & Grow */}
        <section className="bg-[#252528] border border-white/5 rounded-[22px] p-3 sm:p-4" data-testid="reflect-grow-section">
          <h2 className="px-2 pt-2 pb-3 text-[#B5B1AB] text-[12px] tracking-[0.3em] uppercase font-sans">Reflect &amp; Grow</h2>
          <div className="grid grid-cols-2 gap-2.5">
            <div
              className={`rounded-[18px] border p-4 flex flex-col transition-colors ${showGratitude ? 'col-span-2' : ''} ${
                gratitudeCompleted && !showGratitude ? 'bg-gradient-to-br from-[#C6A75E]/10 to-[#2A2A2D] border-[#C6A75E]/30' : 'bg-[#2A2A2D] border-white/5'
              }`}
              data-testid="gratitude-card"
              data-completed={gratitudeCompleted ? 'true' : 'false'}
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  {gratitudeCompleted && !showGratitude
                    ? <Check className="w-5 h-5 text-[#C6A75E]" strokeWidth={2} />
                    : <Sparkles className="w-5 h-5 text-[#C6A75E]" strokeWidth={1.5} />}
                  <span className="font-serif text-[17px] text-[#DCD3CA]">Gratitude</span>
                </div>
                {!showGratitude && gratitudeCompleted && (
                  <button
                    onClick={() => setShowGratitude(true)}
                    className="text-[#7A7670] hover:text-[#C6A75E]"
                    data-testid="review-gratitude-btn"
                    aria-label="Edit gratitude"
                  >
                    <Pencil className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                )}
              </div>

              {showGratitude ? (
                <div className="space-y-3">
                  <p className="text-[#B5B1AB] text-xs font-sans italic leading-relaxed">
                    &ldquo;Practicing gratitude strengthens neural pathways of positivity and shifts your focus toward abundance.&rdquo;
                  </p>
                  <p className="text-[#DCD3CA] text-sm font-sans">Today I am grateful for...</p>
                  {[0, 1, 2, 3, 4].map((idx) => (
                    <Input
                      key={`gratitude-${idx}`}
                      value={gratitudeEntries[idx] || ''}
                      onChange={(e) => {
                        const newEntries = [...gratitudeEntries];
                        newEntries[idx] = e.target.value;
                        setGratitudeEntries(newEntries);
                      }}
                      placeholder={idx < 3 ? `Gratitude ${idx + 1} (required)` : `Gratitude ${idx + 1} (optional)`}
                      className="bg-[#1C1C1E] border-white/10 text-[#DCD3CA]"
                      data-testid={`gratitude-input-${idx}`}
                    />
                  ))}
                  <div className="flex gap-2 pt-1">
                    <Button onClick={saveGratitude} disabled={savingGratitude} className="bg-[#C6A75E] text-[#1C1C1E]" data-testid="save-gratitude-btn">
                      {savingGratitude ? 'Saving...' : (gratitudeCompleted ? 'Update Gratitude' : 'Save Gratitude')}
                    </Button>
                    <Button variant="outline" onClick={() => setShowGratitude(false)} className="border-[#666666] text-[#B5B1AB]" data-testid="cancel-gratitude-btn">
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : gratitudeCompleted ? (
                <div data-testid="gratitude-completed-state" className="flex-1 flex flex-col">
                  <p className="text-[#C6A75E] font-serif text-[15px] leading-snug">Practice complete for today</p>
                  <p className="text-[#7A7670] text-[12px] font-sans mt-1">
                    {gratitudeEntries.filter(g => g.trim()).length} gratitudes saved · returns tomorrow
                  </p>
                  <button
                    onClick={() => setShowGratitudeWeek(true)}
                    className="mt-auto pt-3 text-[#C6A75E] text-xs font-sans hover:underline flex items-center gap-1.5"
                    data-testid="view-gratitude-week-btn"
                  >
                    <Sparkles className="w-3 h-3" strokeWidth={1.5} />
                    Your week in gratitude
                  </button>
                </div>
              ) : (
                <div className="flex-1 flex flex-col">
                  <p className="text-[#7A7670] text-[13px] font-sans leading-snug">What are you grateful for today?</p>
                  <button
                    onClick={() => setShowGratitude(true)}
                    className="mt-3 self-start rounded-full border border-[#C6A75E]/60 px-3.5 py-2 whitespace-nowrap text-[#C6A75E] text-[13px] font-sans hover:bg-[#C6A75E]/10 transition-colors"
                    data-testid="open-gratitude-btn"
                  >
                    + Add gratitude
                  </button>
                  <button
                    onClick={() => setShowGratitudeWeek(true)}
                    className="mt-3 text-[#7A7670] hover:text-[#C6A75E] text-[11px] font-sans text-left"
                    data-testid="view-gratitude-week-btn-empty"
                  >
                    Your week in gratitude
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={() => navigate('/journal')}
              className={`rounded-[18px] bg-[#2A2A2D] border border-white/5 hover:border-white/15 p-4 flex flex-col text-left transition-colors ${showGratitude ? 'col-span-2' : ''}`}
              data-testid="my-journal-btn"
            >
              <div className="flex items-center gap-2 mb-2">
                <Book className="w-5 h-5 text-[#B5B1AB]" strokeWidth={1.5} />
                <span className="font-serif text-[17px] text-[#DCD3CA]">My Journal</span>
              </div>
              <p className="text-[#7A7670] text-[13px] font-sans leading-snug">Capture your thoughts and track your journey.</p>
              {!user?.is_premium && (
                <span className="flex items-center gap-1 text-[#C6A75E] text-xs font-sans mt-2">
                  <Crown className="w-3 h-3" /> Premium
                </span>
              )}
              <span className="mt-auto pt-3 self-start flex items-center gap-1 rounded-full border border-[#C6A75E]/60 px-3.5 py-2 whitespace-nowrap text-[#C6A75E] text-[13px] font-sans">
                Open journal <ChevronRight className="w-3.5 h-3.5" />
              </span>
            </button>
          </div>
        </section>
      </div>

      {/* Future Self Letter Modal */}
      <FutureSelfLetter
        isOpen={showFutureLetter}
        onClose={() => setShowFutureLetter(false)}
        goal={goal}
        onUpdate={(updatedGoal) => setGoal(updatedGoal)}
      />

      {/* Weekly Gratitude Digest Modal */}
      <GratitudeWeekDigest
        isOpen={showGratitudeWeek}
        onClose={() => setShowGratitudeWeek(false)}
      />

      {/* Bottom Navigation */}
      <nav
        className="fixed left-4 right-4 max-w-xl mx-auto bg-[#232326]/95 backdrop-blur-xl border border-white/10 rounded-[30px] h-[70px] flex items-center justify-around px-6 shadow-2xl z-50"
        style={{ bottom: 'calc(1.25rem + env(safe-area-inset-bottom, 0px))' }}
      >
        <button
          className="flex flex-col items-center gap-1 text-[#8BAA9E]"
          data-testid="nav-home"
        >
          <Leaf className="w-6 h-6" strokeWidth={1.5} />
          <span className="text-[12px] font-sans font-medium text-[#DCD3CA]">Home</span>
        </button>
        <button
          onClick={() => navigate('/progress')}
          className="flex flex-col items-center gap-1 text-[#7A7670] hover:text-[#B5B1AB] transition-colors"
          data-testid="nav-progress"
        >
          <BarChart3 className="w-6 h-6" strokeWidth={1.5} />
          <span className="text-[12px] font-sans">Progress</span>
        </button>
        <button
          onClick={() => navigate('/settings')}
          className="flex flex-col items-center gap-1 text-[#7A7670] hover:text-[#B5B1AB] transition-colors"
          data-testid="nav-settings"
        >
          <Settings className="w-6 h-6" strokeWidth={1.5} />
          <span className="text-[12px] font-sans">Settings</span>
        </button>
      </nav>
    </div>
  );
};

export default Dashboard;
